import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { KeyRound, MailPlus, MoreHorizontal, ShieldCheck, UserX, UserCheck } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { RelativeTime } from './relative-time';
import { useIsMobile } from '@/hooks/use-media-query';
import { useTranslation } from 'react-i18next';
import type { Operator } from '@/schemas/users';
import { isPendingInvite } from '@/schemas/users';

interface UsersTableProps {
  data: Operator[];
  isLoading: boolean;
  currentUserId: string | undefined;
  onChangeRole: (user: Operator) => void;
  onResetPassword: (user: Operator) => void;
  onDeactivate: (user: Operator) => void;
  onReactivate: (user: Operator) => void;
  onResendInvite: (user: Operator) => void;
}

function StatusWord({ user }: { user: Operator }) {
  const { t } = useTranslation();
  if (isPendingInvite(user)) {
    return (
      <span className="inline-flex items-center gap-2 whitespace-nowrap text-[13.5px] font-medium text-muted-foreground">
        <span className="h-2 w-2 rounded-full bg-amber-500" aria-hidden="true" />
        {t('operators.table.invited')}
      </span>
    );
  }
  const active = user.active;
  const textClass = active ? 'text-status-active' : 'text-faint';
  const dotClass = active ? 'bg-status-active' : 'bg-faint';
  return (
    <span
      className={`inline-flex items-center gap-2 whitespace-nowrap text-[13.5px] font-medium ${textClass}`}
    >
      <span className={`h-2 w-2 rounded-full ${dotClass}`} aria-hidden="true" />
      {active ? t('common.status.active') : t('common.status.deactivated')}
    </span>
  );
}

function YouBadge() {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center whitespace-nowrap rounded-md bg-muted px-2 py-0.5 text-[12px] font-medium text-muted-foreground">
      {t('operators.youBadge')}
    </span>
  );
}

interface RowActionsProps {
  user: Operator;
  isSelf: boolean;
  onChangeRole: (user: Operator) => void;
  onResetPassword: (user: Operator) => void;
  onDeactivate: (user: Operator) => void;
  onReactivate: (user: Operator) => void;
  onResendInvite: (user: Operator) => void;
}

function RowActions({
  user,
  isSelf,
  onChangeRole,
  onResetPassword,
  onDeactivate,
  onReactivate,
  onResendInvite,
}: RowActionsProps) {
  const { t } = useTranslation();

  const selfGuarded = (item: React.ReactNode) =>
    isSelf ? (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            {/* span keeps the tooltip working on the disabled item */}
            <span>{item}</span>
          </TooltipTrigger>
          <TooltipContent>
            <p className="max-w-[240px] text-xs">{t('operators.actions.selfDisabled')}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : (
      item
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground"
          aria-label={t('operators.table.actionsAria', { username: user.username })}
        >
          <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {selfGuarded(
          <DropdownMenuItem disabled={isSelf} onSelect={() => onChangeRole(user)}>
            <ShieldCheck className="mr-2 h-4 w-4" aria-hidden="true" />
            {t('operators.actions.changeRole')}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={() => onResetPassword(user)}>
          <KeyRound className="mr-2 h-4 w-4" aria-hidden="true" />
          {t('operators.actions.resetPassword')}
        </DropdownMenuItem>
        {!user.active && user.email && (
          <DropdownMenuItem onSelect={() => onResendInvite(user)}>
            <MailPlus className="mr-2 h-4 w-4" aria-hidden="true" />
            {t('operators.actions.resendInvite')}
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        {user.active
          ? selfGuarded(
              <DropdownMenuItem
                disabled={isSelf}
                className="text-destructive focus:text-destructive"
                onSelect={() => onDeactivate(user)}
              >
                <UserX className="mr-2 h-4 w-4" aria-hidden="true" />
                {t('operators.actions.deactivate')}
              </DropdownMenuItem>
            )
          : null}
        {!user.active && (
          <DropdownMenuItem onSelect={() => onReactivate(user)}>
            <UserCheck className="mr-2 h-4 w-4" aria-hidden="true" />
            {t('operators.actions.reactivate')}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function UsersTable({
  data,
  isLoading,
  currentUserId,
  onChangeRole,
  onResetPassword,
  onDeactivate,
  onReactivate,
  onResendInvite,
}: UsersTableProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const columns: ColumnDef<Operator>[] = [
    {
      accessorKey: 'username',
      header: t('operators.table.username'),
      cell: ({ row }) => {
        const user = row.original;
        const isSelf = user.id === currentUserId;
        return (
          <div className="flex items-center gap-2">
            <span className="font-mono text-[13.5px] font-medium text-foreground">
              {user.username}
            </span>
            {isSelf && <YouBadge />}
          </div>
        );
      },
    },
    {
      accessorKey: 'role',
      header: t('operators.table.role'),
      cell: ({ row }) => (
        <span className="text-[13.5px] text-muted-foreground">
          {t(`operators.roles.${row.original.role}`)}
        </span>
      ),
    },
    {
      accessorKey: 'email',
      header: t('operators.table.email'),
      cell: ({ row }) => (
        <span className="text-[13.5px] text-muted-foreground">{row.original.email ?? '—'}</span>
      ),
    },
    {
      accessorKey: 'active',
      header: t('operators.table.status'),
      cell: ({ row }) => <StatusWord user={row.original} />,
    },
    {
      accessorKey: 'createdAt',
      header: t('operators.table.created'),
      cell: ({ row }) => (
        <span className="text-[13.5px] text-muted-foreground">
          <RelativeTime date={row.original.createdAt} />
        </span>
      ),
    },
    {
      id: 'actions',
      header: () => <span className="sr-only">{t('operators.table.actions')}</span>,
      cell: ({ row }) => (
        <div className="flex justify-end">
          <RowActions
            user={row.original}
            isSelf={row.original.id === currentUserId}
            onChangeRole={onChangeRole}
            onResetPassword={onResetPassword}
            onDeactivate={onDeactivate}
            onReactivate={onReactivate}
            onResendInvite={onResendInvite}
          />
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) {
    return (
      <div className="space-y-2 pt-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-11 w-full" />
        ))}
      </div>
    );
  }

  if (isMobile) {
    return (
      <ul className="border-t">
        {data.map((user) => {
          const isSelf = user.id === currentUserId;
          return (
            <li key={user.id} className="border-b px-1 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-[13.5px] font-medium text-foreground">
                      {user.username}
                    </span>
                    {isSelf && <YouBadge />}
                  </div>
                  <p className="mt-1.5 text-[13px] text-muted-foreground">
                    {t(`operators.roles.${user.role}`)}
                  </p>
                  {user.email && (
                    <p className="mt-0.5 truncate text-[13px] text-muted-foreground">
                      {user.email}
                    </p>
                  )}
                  <div className="mt-1.5 flex items-center gap-3 text-[13px] text-faint">
                    <StatusWord user={user} />
                    <RelativeTime date={user.createdAt} />
                  </div>
                </div>
                <div className="shrink-0">
                  <RowActions
                    user={user}
                    isSelf={isSelf}
                    onChangeRole={onChangeRole}
                    onResetPassword={onResetPassword}
                    onDeactivate={onDeactivate}
                    onReactivate={onReactivate}
                    onResendInvite={onResendInvite}
                  />
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <Table className="min-w-[560px] md:min-w-0">
      <TableHeader>
        {table.getHeaderGroups().map((hg) => (
          <TableRow key={hg.id} className="hover:bg-transparent">
            {hg.headers.map((header) => (
              <TableHead key={header.id} className="h-11 px-3 text-[13px] font-medium text-faint">
                {header.isPlaceholder
                  ? null
                  : flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.length ? (
          table.getRowModel().rows.map((row) => (
            <TableRow key={row.id} className="hover:bg-foreground/5">
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} className="px-3 py-3.5">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
              {t('operators.empty')}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
